import { useState, useEffect, useMemo } from "react";
import type { LeadStage } from "@/mocks/leadsData";
import { allStages } from "@/mocks/leadsData";
import type { StageDef, StageExtraFieldDef } from "./MoveToStageModal";

type CallData = {
  duration: number;
  status: "connected" | "missed" | "busy";
};

type Props = {
  leadName: string;
  leadPhone: string;
  currentStage: LeadStage;
  stageDefinitions?: StageDef[];
  stages?: string[];
  callData: CallData | null;
  onClose: () => void;
  onSave: (data: {
    notes: string;
    newStage: LeadStage;
    extraFieldValues: Record<string, string>;
  }) => void;
};

export function PostCallDrawer({
  leadName,
  leadPhone,
  currentStage,
  stageDefinitions = [],
  stages = [],
  callData,
  onClose,
  onSave,
}: Props) {
  const [notes, setNotes] = useState("");
  const [selectedStage, setSelectedStage] = useState<string>(currentStage);
  const [stageDropdownOpen, setStageDropdownOpen] = useState(false);
  const [extraFieldValues, setExtraFieldValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const availableStages = stages.length > 0 ? stages : allStages;

  const targetStageObj = useMemo(() => {
    return stageDefinitions.find((s) => s.name?.toLowerCase().trim() === selectedStage?.toLowerCase().trim());
  }, [stageDefinitions, selectedStage]);

  const extraFields: StageExtraFieldDef[] = useMemo(() => {
    return Array.isArray(targetStageObj?.extraFields) ? targetStageObj.extraFields : [];
  }, [targetStageObj]);

  // Reset extra field values when stage changes
  useEffect(() => {
    setExtraFieldValues({});
    setErrors({});
  }, [selectedStage]);

  const formatDuration = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    if (m === 0) return `${s}s`;
    return `${m}m ${s}s`;
  };

  const updateFieldValue = (fieldKey: string, value: string) => {
    setExtraFieldValues((prev) => ({ ...prev, [fieldKey]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  };

  const normalizeType = (t?: string): "text" | "number" | "currency" | "date" | "time" | "dropdown" | "textarea" | "toggle" => {
    if (!t) return "text";
    const low = t.toLowerCase().trim();
    if (low.includes("num")) return "number";
    if (low.includes("curren") || low.includes("price") || low.includes("amount") || low.includes("deal")) return "currency";
    if (low.includes("date")) return "date";
    if (low.includes("time")) return "time";
    if (low.includes("drop") || low.includes("select")) return "dropdown";
    if (low.includes("area")) return "textarea";
    if (low.includes("toggle") || low.includes("bool") || low.includes("check")) return "toggle";
    return "text";
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    extraFields.forEach((f) => {
      const key = f.label || String(f.id);
      if (f.required && !extraFieldValues[key]?.trim()) {
        newErrors[key] = "Required";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({ notes, newStage: selectedStage as LeadStage, extraFieldValues });
    onClose();
  };

  const statusBadge = () => {
    if (!callData) return { color: "bg-gray-50 text-gray-700", icon: "ri-phone-line", label: "Completed" };
    if (callData.status === "connected") return { color: "bg-green-50 text-green-700", icon: "ri-phone-line", label: "Connected" };
    if (callData.status === "missed") return { color: "bg-red-50 text-red-600", icon: "ri-close-circle-line", label: "Missed" };
    return { color: "bg-amber-50 text-amber-600", icon: "ri-phone-line", label: "Busy" };
  };

  const badge = statusBadge();

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Left overlay */}
      <div className="flex-1 bg-black/20" onClick={onClose} />

      {/* Right drawer */}
      <div className="w-[420px] max-w-[90vw] bg-white border-l border-gray-200 shadow-2xl flex flex-col h-full overflow-hidden animate-slide-in-right">
        {/* Header */}
        <div className="bg-indigo-600 px-5 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-white text-lg">Call Log</h3>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-white cursor-pointer transition-colors"
            >
              <i className="ri-close-line" />
            </button>
          </div>
          <p className="text-white text-sm opacity-90 mt-1">{leadName}</p>
          <p className="text-white text-xs opacity-70">{leadPhone}</p>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto pb-6">
          {/* Section 1: Call Details */}
          {callData && (
            <div className="p-4 mx-4 mt-4 bg-gray-50 rounded-xl border border-gray-100 flex items-center justify-between">
              <div>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
                  <i className={`${badge.icon} text-xs`} />
                  {badge.label}
                </span>
                <p className="text-xs text-gray-500 mt-1">Duration: {formatDuration(callData.duration)}</p>
              </div>
              <p className="text-xs text-gray-400">Just now</p>
            </div>
          )}

          {/* Section 2: Call Notes */}
          <div className="mx-4 mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Call Notes / Outcome <span className="text-gray-400 text-xs">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              placeholder="Write what was discussed during this call..."
              className="w-full border border-gray-200 rounded-xl p-3 h-28 resize-none text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all"
            />
            <p className="text-xs text-gray-400 text-right mt-1">{notes.length}/500</p>
          </div>

          {/* Section 3: Lead Stage */}
          <div className="mx-4 mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Update Lead Stage</label>
            <p className="text-xs text-gray-400 mb-3">Select new stage for this lead after this call</p>

            {/* Stage dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setStageDropdownOpen(!stageDropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 cursor-pointer hover:border-indigo-300 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: targetStageObj?.color || "#6366f1" }}
                  />
                  {selectedStage}
                  {selectedStage === currentStage && (
                    <span className="text-xs text-gray-400">(current)</span>
                  )}
                </span>
                <i className="ri-arrow-down-s-line text-gray-400 w-4 h-4 flex items-center justify-center" />
              </button>

              {stageDropdownOpen && (
                <div className="absolute top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto py-1">
                  {availableStages.map((stage) => {
                    const stDef = stageDefinitions.find((s) => s.name === stage);
                    return (
                      <button
                        key={stage}
                        type="button"
                        onClick={() => {
                          setSelectedStage(stage);
                          setStageDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left hover:bg-indigo-50 transition-colors cursor-pointer ${
                          selectedStage === stage ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-700"
                        }`}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: stDef?.color || "#6366f1" }}
                        />
                        {stage}
                        {stage === currentStage && (
                          <span className="text-xs text-gray-400 ml-auto">current</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Extra Fields — dynamically rendered when stage has configured extraFields */}
            {extraFields.length > 0 && (
              <div className="bg-indigo-50/70 border border-indigo-200 rounded-xl p-4 mt-3 animate-slide-in">
                <p className="font-semibold text-indigo-900 text-xs uppercase tracking-wider mb-3">
                  Required Information for {selectedStage}
                </p>
                <div className="space-y-3">
                  {extraFields.map((field) => {
                    const fieldKey = field.label || String(field.id);
                    const fType = normalizeType(field.type);
                    const hasError = !!errors[fieldKey];

                    return (
                      <div key={fieldKey}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-0.5">*</span>}
                        </label>

                        {fType === "number" && (
                          <input
                            type="number"
                            value={extraFieldValues[fieldKey] || ""}
                            onChange={(e) => updateFieldValue(fieldKey, e.target.value)}
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                            className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all ${
                              hasError ? "border-red-400 bg-red-50" : "border-gray-200 bg-white"
                            }`}
                          />
                        )}

                        {fType === "currency" && (
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">₹</span>
                            <input
                              type="text"
                              value={extraFieldValues[fieldKey] || ""}
                              onChange={(e) => updateFieldValue(fieldKey, e.target.value)}
                              placeholder="0"
                              className={`w-full border rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all ${
                                hasError ? "border-red-400 bg-red-50" : "border-gray-200 bg-white"
                              }`}
                            />
                          </div>
                        )}

                        {fType === "date" && (
                          <input
                            type="date"
                            value={extraFieldValues[fieldKey] || ""}
                            onChange={(e) => updateFieldValue(fieldKey, e.target.value)}
                            className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all ${
                              hasError ? "border-red-400 bg-red-50" : "border-gray-200 bg-white"
                            }`}
                          />
                        )}

                        {fType === "time" && (
                          <input
                            type="time"
                            value={extraFieldValues[fieldKey] || ""}
                            onChange={(e) => updateFieldValue(fieldKey, e.target.value)}
                            className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all ${
                              hasError ? "border-red-400 bg-red-50" : "border-gray-200 bg-white"
                            }`}
                          />
                        )}

                        {fType === "dropdown" && (
                          <select
                            value={extraFieldValues[fieldKey] || ""}
                            onChange={(e) => updateFieldValue(fieldKey, e.target.value)}
                            className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all bg-white cursor-pointer ${
                              hasError ? "border-red-400 bg-red-50" : "border-gray-200"
                            }`}
                          >
                            <option value="">Select {field.label}...</option>
                            {(field.options || []).map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        )}

                        {fType === "textarea" && (
                          <textarea
                            value={extraFieldValues[fieldKey] || ""}
                            onChange={(e) => updateFieldValue(fieldKey, e.target.value)}
                            placeholder={`Enter ${field.label.toLowerCase()}...`}
                            rows={3}
                            className={`w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all ${
                              hasError ? "border-red-400 bg-red-50" : "border-gray-200 bg-white"
                            }`}
                          />
                        )}

                        {fType === "toggle" && (
                          <div className="flex items-center gap-3 py-1">
                            <button
                              type="button"
                              onClick={() =>
                                updateFieldValue(
                                  fieldKey,
                                  extraFieldValues[fieldKey] === "true" ? "false" : "true"
                                )
                              }
                              className={`relative w-10 h-5 rounded-full transition-colors duration-200 cursor-pointer ${
                                extraFieldValues[fieldKey] === "true" ? "bg-indigo-600" : "bg-gray-300"
                              }`}
                            >
                              <span
                                className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-xs transition-transform duration-200 ${
                                  extraFieldValues[fieldKey] === "true" ? "translate-x-5" : "translate-x-0"
                                }`}
                              />
                            </button>
                            <span className="text-xs text-gray-600 font-medium">
                              {extraFieldValues[fieldKey] === "true" ? "Yes" : "No"}
                            </span>
                          </div>
                        )}

                        {fType === "text" && (
                          <input
                            type="text"
                            value={extraFieldValues[fieldKey] || ""}
                            onChange={(e) => updateFieldValue(fieldKey, e.target.value)}
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                            className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all ${
                              hasError ? "border-red-400 bg-red-50" : "border-gray-200 bg-white"
                            }`}
                          />
                        )}

                        {hasError && (
                          <p className="text-xs text-red-500 mt-1 font-medium">{errors[fieldKey]}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-white cursor-pointer transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg cursor-pointer transition-colors shadow-xs"
          >
            Save Call Log
          </button>
        </div>
      </div>
    </div>
  );
}